// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Badge, Button, Drawer, Group, Loader, Paper, Stack, Tabs, Text, Title } from '@mantine/core';
import { normalizeErrorString } from '@medplum/core';
import type { Composition, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconFileDescription } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { IntakeScreenerModal } from '../../components/encounter/IntakeScreenerModal';
import { CompletedIntakeScreenerModal } from '../../components/encounter/SupplementalInfoSection';
import type { TriageCompositionData } from '../../hooks/useTriageQueue';

interface TriagePatientDrawerProps {
  composition: TriageCompositionData | null;
  opened: boolean;
  onClose: () => void;
}

function patientMrn(patient: Patient): string | undefined {
  const value = patient.identifier?.find((identifier) => identifier.value)?.value;
  if (!value) {
    return undefined;
  }
  if (value.startsWith('MRN-')) {
    return value;
  }
  return `MRN-${value}`;
}

const BIRTHSEX_EXTENSION_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';

export function patientSexLabel(patient: Patient): string {
  const birthSex = patient.extension?.find((extension) => extension.url === BIRTHSEX_EXTENSION_URL);
  const code = birthSex?.valueCoding?.code ?? birthSex?.valueCode;
  if (code === 'M') {
    return 'Male';
  }
  if (code === 'F') {
    return 'Female';
  }
  if (code === '32570681000036106') {
    return 'Intersex';
  }
  if (code === 'OTH') {
    return 'Other';
  }
  if (code === 'ASKU') {
    return 'Asked but unknown';
  }
  if (code === 'UNK' || code === 'U') {
    return 'Unknown';
  }
  if (code === 'asked-declined') {
    return 'Asked but declined';
  }
  if (birthSex) {
    return 'Unknown';
  }
  if (patient.gender) {
    return patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1);
  }
  return 'Unknown';
}

function patientAge(birthDate: string | undefined): number | undefined {
  if (!birthDate) {
    return undefined;
  }
  const birth = new Date(`${birthDate}T00:00:00`);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const birthdayPassed =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!birthdayPassed) {
    age -= 1;
  }
  return age;
}

function formatLongDate(value: string | undefined): string {
  if (!value) {
    return 'Unavailable';
  }
  return new Date(value).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatScore(value: number | undefined, maximum: number): string {
  return value === undefined ? `- / ${maximum}` : `${value} / ${maximum}`;
}

function recommendationLabel(value: string | undefined): string {
  if (!value) {
    return 'Pending review';
  }
  if (value.toLowerCase().includes('eligible') && !value.toLowerCase().includes('not')) {
    return 'Probably HSAT eligible';
  }
  return value.replaceAll('-', ' ');
}

function profileStatus(patient: Patient): string {
  const statusTag = patient.meta?.tag?.find((tag) => tag.code === 'prospective' || tag.code === 'active');
  return statusTag?.code === 'active' ? 'Active' : 'Prospective';
}

function isIntakeCompleted(patient: Patient): boolean {
  return (
    patient.meta?.tag?.some(
      (tag) => tag.system === 'http://polarissleep.com/tags' && tag.code === 'eligibility-check-finished'
    ) ?? false
  );
}

function questionnaireLabel(reference: string): string {
  const name = reference.split('/').pop() ?? reference;
  if (name.includes('stop-bang')) {
    return 'STOP-BANG';
  }
  if (name.includes('epworth')) {
    return 'Epworth Sleepiness Scale';
  }
  if (name.includes('sleep-history')) {
    return 'Sleep history questionnaire';
  }
  return 'Intake questionnaire';
}

export function TriagePatientDrawer({ composition, opened, onClose }: TriagePatientDrawerProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient>();
  const [intakeComposition, setIntakeComposition] = useState<Composition>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [screenerOpened, setScreenerOpened] = useState(false);

  useEffect(() => {
    if (!opened || !composition?.patientId) {
      return;
    }
    setLoading(true);
    setError(undefined);
    Promise.all([
      medplum.readResource('Patient', composition.patientId),
      composition.compositionId ? medplum.readResource('Composition', composition.compositionId) : undefined,
    ])
      .then(([loadedPatient, loadedComposition]) => {
        setPatient(loadedPatient);
        setIntakeComposition(loadedComposition);
      })
      .catch((loadError) => setError(normalizeErrorString(loadError)))
      .finally(() => setLoading(false));
  }, [composition, medplum, opened]);

  const questionnaireNames = (intakeComposition?.section ?? [])
    .flatMap((section) => section.entry ?? [])
    .map((entry) => entry.reference)
    .filter((reference): reference is string => Boolean(reference?.startsWith('QuestionnaireResponse/')))
    .map(questionnaireLabel);
  const uniqueQuestionnaires = [...new Set(questionnaireNames)];
  const missingQuestionnaire = uniqueQuestionnaires.length === 0;
  const intakeCompleted = patient ? isIntakeCompleted(patient) : false;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(720px, 100vw)"
      withCloseButton
      title={
        patient ? (
          <Stack gap={0}>
            <Title order={2}>
              {patient.name?.[0]?.text ??
                `${patient.name?.[0]?.given?.join(' ') ?? ''} ${patient.name?.[0]?.family ?? ''}`}
            </Title>
            <Text size="sm" c="dimmed">
              {patientMrn(patient) ?? 'MRN unavailable'} · {patientAge(patient.birthDate) ?? 'Age unavailable'}
            </Text>
          </Stack>
        ) : (
          (composition?.patientName ?? 'Patient')
        )
      }
      overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
      styles={{ body: { padding: 0 }, header: { padding: '18px 24px' } }}
    >
      <Tabs
        defaultValue="screener"
        keepMounted={false}
        variant="pills"
        color="polaris"
        styles={{ root: { padding: '14px 24px 0' } }}
      >
        <Tabs.List>
          <Tabs.Tab value="screener">Screener</Tabs.Tab>
          <Tabs.Tab value="profile">Profile</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="screener" pt="lg" px="0">
          {loading && <Loader size="sm" />}
          {error && <Alert color="red">{error}</Alert>}
          {!loading && !error && (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Screener submitted {formatLongDate(composition?.date)}
              </Text>
              <Paper withBorder radius="md" shadow="sm" p="md">
                <Text size="xs" fw={700} tt="uppercase" c="grape" mb="sm">
                  Screening scores
                </Text>
                <Group grow align="stretch">
                  {[
                    [
                      'STOP-BANG',
                      formatScore(composition?.stopBangScore, 8),
                      composition?.stopBangScore !== undefined ? 'Intermediate' : 'Unavailable',
                    ],
                    [
                      'Epworth (ESS)',
                      formatScore(composition?.epworthScore, 24),
                      composition?.epworthScore !== undefined ? 'Normal range' : 'Unavailable',
                    ],
                    [
                      'BMI',
                      composition?.bmi === undefined ? '-' : composition.bmi.toFixed(1),
                      composition?.bmi !== undefined ? 'Overweight' : 'Unavailable',
                    ],
                  ].map(([label, value, detail]) => (
                    <Paper key={label} withBorder radius="sm" p="sm">
                      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                        {label}
                      </Text>
                      <Text size="xl" fw={700}>
                        {value}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {detail}
                      </Text>
                    </Paper>
                  ))}
                </Group>
              </Paper>
              {missingQuestionnaire && (
                <Alert color="grape" title="Manual review required">
                  Some required questionnaires are incomplete for this patient.
                </Alert>
              )}
              <Paper withBorder radius="md" shadow="sm" p="md">
                <Text size="xs" fw={700} tt="uppercase" c="grape" mb="sm">
                  Clinical flags
                </Text>
                <Text size="sm" fw={600} c="dimmed">
                  Comorbidities
                </Text>
                <Group gap="xs" mt={4} mb="sm">
                  {composition?.comorbidities.length ? (
                    composition.comorbidities.map((item) => (
                      <Badge key={item.display} variant="light">
                        {item.display}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="light" color="gray">
                      None
                    </Badge>
                  )}
                </Group>
                <Text size="sm" fw={600} c="dimmed">
                  Exclusions
                </Text>
                <Group gap="xs" mt={4}>
                  {composition?.exclusions.length ? (
                    composition.exclusions.map((item) => (
                      <Badge key={item.display} variant="light" color="orange">
                        {item.display}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="light" color="gray">
                      None
                    </Badge>
                  )}
                </Group>
              </Paper>
              <Paper withBorder radius="md" shadow="sm" p="md">
                <Text size="xs" fw={700} tt="uppercase" c="grape" mb="sm">
                  Eligibility recommendation
                </Text>
                <Badge color="green" variant="light">
                  ✓ {recommendationLabel(composition?.recommendation)}
                </Badge>
                {composition?.triageSummary && (
                  <Alert color="green" mt="md">
                    {composition.triageSummary}
                  </Alert>
                )}
              </Paper>
              <Button
                variant={intakeCompleted ? 'outline' : 'filled'}
                color="polaris"
                fullWidth
                leftSection={!intakeCompleted ? <IconFileDescription size={16} /> : undefined}
                onClick={() => setScreenerOpened(true)}
              >
                {intakeCompleted ? 'Open full screener  →' : 'Fill questionnaires'}
              </Button>
              {!intakeCompleted && (
                <Text size="sm" c="dimmed" ta="center">
                  Completing questionnaires opens the full intake flow.
                </Text>
              )}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="profile" pt="lg" px="0">
          {patient ? (
            <Stack gap="md">
              <Paper withBorder radius="md" shadow="sm" p="md">
                {[
                  [
                    'Date of birth',
                    patient.birthDate
                      ? `${new Date(`${patient.birthDate}T00:00:00`).toLocaleDateString()} (${patientAge(patient.birthDate) ?? '-'})`
                      : 'Unavailable',
                  ],
                  ['MRN', patientMrn(patient) ?? 'Unavailable'],
                  ['Sex', patientSexLabel(patient)],
                  ['Patient status', profileStatus(patient)],
                  ['Last active', formatLongDate(patient.meta?.lastUpdated)],
                ].map(([label, value]) => (
                  <Group
                    key={label}
                    justify="space-between"
                    py="sm"
                    style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                  >
                    <Text size="sm" fw={600} c="dimmed">
                      {label}
                    </Text>
                    {label === 'Patient status' ? (
                      <Badge color="grape" variant="light">
                        {value}
                      </Badge>
                    ) : (
                      <Text size="sm" fw={600}>
                        {value}
                      </Text>
                    )}
                  </Group>
                ))}
              </Paper>
              <Button
                color="polaris"
                fullWidth
                onClick={() => {
                  onClose();
                  navigate(`/Patient/${patient.id}`);
                }}
              >
                Open full chart&nbsp; →
              </Button>
              <Text size="sm" c="dimmed" ta="center">
                The full chart is a multi-tab record — opening it leaves the queue.
              </Text>
            </Stack>
          ) : (
            <Loader size="sm" />
          )}
        </Tabs.Panel>
      </Tabs>
      {patient && (
        <>
          {intakeCompleted ? (
            <CompletedIntakeScreenerModal
              patientId={patient.id ?? ''}
              opened={screenerOpened}
              onClose={() => setScreenerOpened(false)}
            />
          ) : (
            <IntakeScreenerModal
              patient={patient}
              opened={screenerOpened}
              onClose={() => setScreenerOpened(false)}
              onCompleted={() => {
                setScreenerOpened(false);
                medplum
                  .readResource('Patient', patient.id ?? '', { cache: 'no-cache' })
                  .then(setPatient)
                  .catch(() => undefined);
              }}
            />
          )}
        </>
      )}
    </Drawer>
  );
}
